import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import client from "../lib/api";
import { Panel, DemoTag, Spinner, AiText } from "../components/ui-kit";
import { Flame, Phone, Mail, Sparkles, Plus, X, Send, Clock, CheckCircle2, CalendarClock, Search, StickyNote, Bell } from "lucide-react";
import { toast } from "sonner";
import { FileSpreadsheet, Upload } from "lucide-react";
import { SheetSync } from "../components/leads/SheetSync";
import { ImportLeads } from "../components/leads/ImportLeads";

const STAGES = ["New Lead", "Contacted", "Qualified", "Free Community", "Interested", "Broker Link Sent", "Account Application", "Verification Pending", "Broker Verified", "Premium Activated", "Renewal Due"];
const SOURCES = ["Facebook", "Instagram", "Website", "Telegram", "Manual", "Referral"];

const scoreColor = (s) => (s >= 80 ? "text-bear" : s >= 60 ? "text-gold" : "text-slate-400");
const todayStr = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Dubai" });
const fuState = (l) => {
  const nf = l.next_followup;
  if (!nf) return null;
  const day = nf.slice(0, 10), t = todayStr();
  return day < t ? "overdue" : day === t ? "today" : "upcoming";
};
const daysSince = (iso) => iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86400000) : null;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString() : "—";
const relTime = (iso) => {
  if (!iso) return "";
  const m = (Date.now() - new Date(iso).getTime()) / 60000;
  if (m < 60) return `${Math.max(0, Math.floor(m))}m ago`;
  if (m < 1440) return `${Math.floor(m / 60)}h ago`;
  return `${Math.floor(m / 1440)}d ago`;
};
const ACT_ICON = { note: "🗒️", stage: "↗️", reminder: "⏰", contacted: "✅", message: "✉️" };

export default function Leads() {
  const [leads, setLeads] = useState(null);
  const [sel, setSel] = useState(null);
  const [analysis, setAnalysis] = useState({ text: "", loading: false });
  const [fu, setFu] = useState({ draft: "", loading: false });
  const [tgChat, setTgChat] = useState("");
  const [botUser, setBotUser] = useState("");
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", country: "", interest: "Both", source: "Manual" });
  const [view, setView] = useState("pipeline");
  const [q, setQ] = useState("");
  const [fStage, setFStage] = useState("");
  const [fSource, setFSource] = useState("");
  const [sort, setSort] = useState("recent");
  const [note, setNote] = useState("");
  const [adminChat, setAdminChat] = useState("");
  const [showDigest, setShowDigest] = useState(false);
  const [panel, setPanel] = useState(null);

  const load = () => client.get("/leads").then(({ data }) => setLeads(data));
  useEffect(() => {
    load();
    client.get("/telegram/webhook/status").then(({ data }) => setBotUser(data.bot_username || "")).catch(() => {});
    client.get("/leads/digest-config").then(({ data }) => setAdminChat(data.admin_chat_id || "")).catch(() => {});
  }, []);

  const refreshSel = async (id) => { const { data } = await client.get("/leads"); setLeads(data); const u = data.find((x) => x.id === id); if (u) setSel(u); };

  const setReminder = async (when) => {
    try { await client.post(`/leads/${sel.id}/reminder`, { when }); toast.success(when === "clear" ? "Reminder cleared" : "Reminder set"); refreshSel(sel.id); }
    catch { toast.error("Failed"); }
  };
  const markContacted = async () => {
    try { await client.post(`/leads/${sel.id}/contacted`, {}); toast.success("Marked contacted"); refreshSel(sel.id); }
    catch { toast.error("Failed"); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    try { await client.post(`/leads/${sel.id}/note`, { text: note }); setNote(""); toast.success("Note added"); refreshSel(sel.id); }
    catch { toast.error("Failed"); }
  };
  const saveDigest = async () => {
    try { await client.post("/leads/digest-config", { admin_chat_id: adminChat }); toast.success("Daily digest chat saved"); setShowDigest(false); }
    catch { toast.error("Failed"); }
  };
  const sendDigestNow = async () => {
    try { await client.post("/leads/digest-config", { admin_chat_id: adminChat }); await client.post("/leads/digest-send-now"); toast.success("Briefing bhej di gayi! Telegram check karo 📲"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to send"); }
  };

  const selectLead = (l) => {
    setSel(l); setAnalysis({ text: "", loading: false }); setTgChat(l.telegram_chat_id || "");
    if (l.pending_followup?.draft) setFu({ draft: l.pending_followup.draft, loading: false, broker_link: l.pending_followup.broker_link, broker: l.pending_followup.broker });
    else setFu({ draft: "", loading: false });
  };
  const copyConnect = (l) => { const link = `https://t.me/${botUser}?start=${l.id}`; navigator.clipboard?.writeText(link); toast.success("Connect link copied"); };

  const move = async (lead, status) => {
    await client.patch(`/leads/${lead.id}`, { status });
    toast.success(`Moved to ${status}`);
    if (sel?.id === lead.id) await refreshSel(lead.id); else load();
  };
  const rescore = async (lead) => {
    setAnalysis({ text: "", loading: true });
    try { const { data } = await client.post(`/leads/${lead.id}/score`); setAnalysis({ text: data.analysis, loading: false }); }
    catch { setAnalysis({ text: "", loading: false }); toast.error("Failed"); }
  };
  const genFollowup = async () => {
    setFu({ draft: "", loading: true });
    try { const { data } = await client.post(`/leads/${sel.id}/followup`); setFu({ draft: data.draft, loading: false, broker_link: data.broker_link, broker: data.broker }); }
    catch { setFu({ draft: "", loading: false }); toast.error("Failed to generate follow-up"); }
  };
  const saveChat = async () => {
    try { await client.patch(`/leads/${sel.id}`, { telegram_chat_id: tgChat }); toast.success("Telegram chat ID saved"); setSel({ ...sel, telegram_chat_id: tgChat }); load(); }
    catch { toast.error("Failed"); }
  };
  const sendTg = async () => {
    try { await client.post(`/leads/${sel.id}/send-telegram`, { text: fu.draft }); toast.success("Follow-up sent to Telegram"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to send"); }
  };
  const addLead = async (e) => {
    e.preventDefault();
    try { await client.post("/leads", form); toast.success("Lead added"); setAdding(false); setForm({ name: "", email: "", phone: "", country: "", interest: "Both", source: "Manual" }); load(); }
    catch { toast.error("Failed"); }
  };
  const bulkDraft = async () => {
    const t = toast.loading("Drafting follow-ups for hot leads…");
    try {
      const { data } = await client.post("/leads/followup/bulk");
      toast.dismiss(t);
      if (data.queued === 0) toast.info(data.note || "No hot leads to draft for");
      else { toast.success(`Drafting ${data.queued} follow-ups — appearing shortly ✍️`); setTimeout(load, Math.min(data.queued * 2500 + 3000, 30000)); }
    } catch { toast.dismiss(t); toast.error("Failed"); }
  };

  if (!leads) return <Layout title="CRM / Leads"><Spinner /></Layout>;

  const hotCount = leads.filter((l) => l.score >= 80).length;
  const overdueCount = leads.filter((l) => fuState(l) === "overdue").length;
  const todayCount = leads.filter((l) => fuState(l) === "today").length;

  const filtered = leads.filter((l) => {
    if (fStage && l.status !== fStage) return false;
    // Telegram leads are stored as telegram_bot / telegram_join — match them all under "Telegram"
    if (fSource && l.source !== fSource && !(fSource === "Telegram" && (l.source || "").startsWith("telegram"))) return false;
    if (q) { const s = `${l.name} ${l.phone || ""} ${l.email || ""}`.toLowerCase(); if (!s.includes(q.toLowerCase())) return false; }
    return true;
  }).sort((a, b) => {
    if (sort === "score") return (b.score || 0) - (a.score || 0);
    if (sort === "name") return (a.name || "").localeCompare(b.name || "");
    if (sort === "followup") return (a.next_followup || "9999") < (b.next_followup || "9999") ? -1 : 1;
    return (b.created_at || "") < (a.created_at || "") ? -1 : 1;
  });

  const fuGroups = { overdue: [], today: [], upcoming: [] };
  leads.forEach((l) => { const s = fuState(l); if (s) fuGroups[s].push(l); });
  Object.values(fuGroups).forEach((arr) => arr.sort((a, b) => (a.next_followup || "") < (b.next_followup || "") ? -1 : 1));

  const Badge = ({ l }) => {
    const s = fuState(l);
    if (s === "overdue") return <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-400 border border-red-500/30">OVERDUE</span>;
    if (s === "today") return <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/30">TODAY</span>;
    return null;
  };

  const FuRow = ({ l }) => {
    const stale = daysSince(l.last_contact);
    return (
      <button onClick={() => selectLead(l)} data-testid={`fu-row-${l.id}`} className="w-full surface surface-hover p-3 text-left flex items-center gap-3 transition-colors duration-200">
        <span className={`font-mono text-xs ${scoreColor(l.score)}`}>{l.score}</span>
        <div className="flex-1 min-w-0">
          <div className="text-sm text-white truncate">{l.name}</div>
          <div className="text-[11px] text-slate-500">{l.status} · {l.interest} · {l.source}</div>
        </div>
        <div className="text-right">
          <div className="text-[11px] text-slate-300">{fmtDate(l.next_followup)}</div>
          {stale != null && <div className="text-[10px] text-slate-600">last {stale}d ago</div>}
        </div>
      </button>
    );
  };

  return (
    <Layout title="CRM / Leads">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="text-sm text-slate-400">{leads.length} leads · {hotCount} hot</span>
        {overdueCount > 0 && <span className="text-xs px-2 py-0.5 rounded bg-red-500/15 text-red-400 border border-red-500/30">{overdueCount} overdue</span>}
        {todayCount > 0 && <span className="text-xs px-2 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/30">{todayCount} due today</span>}
        <DemoTag />
        <button data-testid="digest-config-btn" onClick={() => setShowDigest(true)} className="ml-auto px-3 py-1.5 text-xs flex items-center gap-1.5 border border-[color:var(--border-subtle)] text-slate-300 hover:border-gold transition-colors duration-200"><Bell size={13} />Daily DM</button>
        <button data-testid="bulk-followup-btn" onClick={bulkDraft} disabled={hotCount === 0} className="px-3 py-1.5 text-xs flex items-center gap-1.5 border border-gold text-gold hover:bg-[rgba(212,175,55,0.1)] transition-colors duration-200 disabled:opacity-50"><Sparkles size={14} />Draft hot ({hotCount})</button>
        <button data-testid="sheet-sync-btn" onClick={() => setPanel("sheet")} className="px-3 py-1.5 text-xs flex items-center gap-1.5 border border-[color:var(--border-subtle)] text-slate-300 hover:border-gold transition-colors duration-200"><FileSpreadsheet size={13} />Google Sheet</button>
        <button data-testid="import-leads-btn" onClick={() => setPanel("import")} className="px-3 py-1.5 text-xs flex items-center gap-1.5 border border-[color:var(--border-subtle)] text-slate-300 hover:border-gold transition-colors duration-200"><Upload size={13} />Upload CSV</button>
        <button data-testid="add-lead-btn" onClick={() => setAdding(true)} className="btn-gold px-3 py-1.5 text-xs flex items-center gap-1.5"><Plus size={14} />Add Lead</button>
      </div>
      {panel === "sheet" && <SheetSync onClose={() => setPanel(null)} />}
      {panel === "import" && <ImportLeads onClose={() => setPanel(null)} onDone={load} />}

      {/* Tabs */}
      <div className="flex border border-[color:var(--border-subtle)] w-fit mb-4">
        <button onClick={() => setView("pipeline")} data-testid="tab-pipeline" className={`px-4 py-2 text-sm transition-colors duration-200 ${view === "pipeline" ? "bg-gold text-black font-semibold" : "text-slate-400 hover:text-white"}`}>Pipeline</button>
        <button onClick={() => setView("followups")} data-testid="tab-followups" className={`px-4 py-2 text-sm flex items-center gap-2 transition-colors duration-200 ${view === "followups" ? "bg-gold text-black font-semibold" : "text-slate-400 hover:text-white"}`}>
          Follow-ups {(overdueCount + todayCount) > 0 && <span className={`text-[10px] px-1.5 rounded-full ${view === "followups" ? "bg-black/20" : "bg-red-500/20 text-red-400"}`}>{overdueCount + todayCount}</span>}
        </button>
      </div>

      {view === "pipeline" && (
        <>
          <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 mb-4">
            <div className="relative col-span-2">
              <Search size={14} className="absolute left-2.5 top-2.5 text-slate-500" />
              <input data-testid="lead-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / phone / email" className="pl-8 pr-3 py-2 text-sm bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] text-white focus:border-gold focus:outline-none w-full sm:w-64" />
            </div>
            <select data-testid="filter-stage" value={fStage} onChange={(e) => setFStage(e.target.value)} className="px-3 py-2 text-sm bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] text-white"><option value="">All stages</option>{STAGES.map((s) => <option key={s} value={s}>{s}</option>)}</select>
            <select data-testid="filter-source" value={fSource} onChange={(e) => setFSource(e.target.value)} className="px-3 py-2 text-sm bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] text-white"><option value="">All sources</option>{SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}</select>
            <select data-testid="sort-leads" value={sort} onChange={(e) => setSort(e.target.value)} className="col-span-2 sm:col-span-1 px-3 py-2 text-sm bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] text-white"><option value="recent">Newest</option><option value="score">Top score</option><option value="followup">Follow-up date</option><option value="name">Name A-Z</option></select>
          </div>
          <div className="overflow-x-auto pb-4">
            <div className="flex gap-3 min-w-max">
              {STAGES.map((stage, idx) => {
                const items = filtered.filter((l) => l.status === stage || (idx === 0 && !STAGES.includes(l.status)));
                return (
                  <div key={stage} className="w-64 shrink-0" data-testid={`stage-${stage.replace(/\s/g, "-")}`}>
                    <div className="flex items-center justify-between px-2 py-2 border-b-2 border-gold mb-2">
                      <span className="text-xs uppercase tracking-wider text-slate-300">{stage}</span>
                      <span className="font-mono text-xs text-slate-500">{items.length}</span>
                    </div>
                    <div className="space-y-2">
                      {items.map((l) => (
                        <button key={l.id} onClick={() => selectLead(l)} data-testid={`lead-card-${l.id}`}
                          className="w-full surface surface-hover p-3 text-left transition-colors duration-200">
                          <div className="flex items-center justify-between gap-1">
                            <span className="text-sm text-white truncate flex items-center gap-1">{l.pending_followup?.draft && <span title="AI follow-up ready" className="text-gold">✍️</span>}{l.name}</span>
                            <span className={`font-mono text-xs flex items-center gap-0.5 ${scoreColor(l.score)}`}>{l.score >= 80 && <Flame size={11} />}{l.score}</span>
                          </div>
                          <div className="text-xs text-slate-500 mt-1 flex items-center gap-1.5">
                            <span className={l.interest === "Gold" ? "text-gold" : l.interest === "Bitcoin" ? "text-btc" : "text-slate-400"}>{l.interest}</span>· {l.source}
                            {l.telegram_chat_id && <Send size={10} className="text-bull ml-auto" title="Telegram connected" />}
                          </div>
                          {l.telegram_chat_id && <div data-testid={`lead-tg-${l.id}`} className="text-[11px] font-mono text-slate-400 mt-1 break-all">{l.telegram_username ? `@${l.telegram_username} · ` : ""}ID {l.telegram_chat_id}</div>}
                          <div className="mt-1.5"><Badge l={l} /></div>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {view === "followups" && (
        <div className="grid md:grid-cols-3 gap-4" data-testid="followups-view">
          {[["overdue", "🔴 Overdue", "text-red-400"], ["today", "🟡 Due Today", "text-amber-400"], ["upcoming", "🟢 Upcoming", "text-emerald-400"]].map(([key, label, cls]) => (
            <div key={key}>
              <div className={`text-sm font-head font-semibold mb-2 ${cls}`} data-testid={`fu-col-${key}`}>{label} ({fuGroups[key].length})</div>
              <div className="space-y-2">
                {fuGroups[key].length === 0 ? <div className="surface p-4 text-xs text-slate-500">Nothing here.</div> : fuGroups[key].map((l) => <FuRow key={l.id} l={l} />)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Lead detail */}
      {sel && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="lead-detail">
          <div className="absolute inset-0 bg-black/60" onClick={() => setSel(null)} />
          <div className="relative w-full max-w-md h-full surface overflow-y-auto p-5">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-head text-xl text-white">{sel.name}</h3>
                <div className={`font-mono text-sm mt-1 flex items-center gap-1 ${scoreColor(sel.score)}`}>{sel.score >= 80 && <Flame size={13} />}Score {sel.score}</div>
              </div>
              <button onClick={() => setSel(null)} className="text-slate-400 hover:text-white"><X size={20} /></button>
            </div>
            <div className="mt-4 space-y-2 text-sm">
              <Info k="Email" v={sel.email} icon={<Mail size={13} />} />
              <Info k="Phone" v={sel.phone} icon={<Phone size={13} />} />
              <Info k="Country" v={sel.country} />
              <Info k="Capital" v={sel.capital} />
              <Info k="Interest" v={sel.interest} />
              <Info k="Source / Campaign" v={`${sel.source} / ${sel.campaign || "—"}`} />
              <Info k="Assigned" v={sel.assigned_to || "Unassigned"} />
              <Info k="Broker Interest" v={sel.broker_interest || "—"} />
              <Info k="Last Message" v={sel.last_message || "—"} />
            </div>

            <div className="mt-4">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Score Reasons</div>
              {(sel.score_reasons || []).map((r, i) => <div key={i} className="text-xs text-slate-300 flex gap-2"><span className="text-gold">›</span>{r}</div>)}
            </div>

            <button data-testid="rescore-btn" onClick={() => rescore(sel)} className="mt-4 w-full py-2 text-xs flex items-center justify-center gap-1.5 border border-gold text-gold hover:bg-[rgba(212,175,55,0.1)] transition-colors duration-200"><Sparkles size={13} />AI Re-score & Explain</button>
            {(analysis.loading || analysis.text) && <div className="mt-3 p-3 bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)]">{analysis.loading ? <Spinner /> : <AiText text={analysis.text} />}</div>}

            <div className="mt-5">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Move to stage</div>
              <select data-testid="lead-stage-select" value={sel.status} onChange={(e) => move(sel, e.target.value)} className="w-full bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-3 py-2 text-sm text-white focus:border-gold focus:outline-none">
                {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div className="mt-5 border-t border-[color:var(--border-subtle)] pt-4">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5"><Clock size={12} />Follow-up & Quick Actions</div>
              <div className="text-[11px] mb-2">
                {sel.next_followup
                  ? <span className={fuState(sel) === "overdue" ? "text-red-400" : fuState(sel) === "today" ? "text-amber-400" : "text-slate-300"}>Next follow-up: {new Date(sel.next_followup).toLocaleString()} {fuState(sel) === "overdue" && "(overdue)"}</span>
                  : <span className="text-slate-500">No reminder set</span>}
                {sel.last_contact && <span className="text-slate-600"> · last contact {relTime(sel.last_contact)}</span>}
              </div>
              <div className="flex flex-wrap gap-2">
                <button data-testid="mark-contacted" onClick={markContacted} className="px-2.5 py-1.5 text-xs flex items-center gap-1 border border-[color:var(--border-subtle)] text-slate-200 hover:border-gold transition-colors duration-200"><CheckCircle2 size={13} />Mark contacted</button>
                <button data-testid="remind-tomorrow" onClick={() => setReminder("tomorrow")} className="px-2.5 py-1.5 text-xs flex items-center gap-1 border border-[color:var(--border-subtle)] text-slate-200 hover:border-gold transition-colors duration-200"><CalendarClock size={13} />Tomorrow</button>
                <button data-testid="remind-3d" onClick={() => setReminder("3d")} className="px-2.5 py-1.5 text-xs border border-[color:var(--border-subtle)] text-slate-200 hover:border-gold transition-colors duration-200">+3 days</button>
                <button data-testid="remind-1w" onClick={() => setReminder("1w")} className="px-2.5 py-1.5 text-xs border border-[color:var(--border-subtle)] text-slate-200 hover:border-gold transition-colors duration-200">+1 week</button>
                {sel.next_followup && <button data-testid="remind-clear" onClick={() => setReminder("clear")} className="px-2.5 py-1.5 text-xs border border-[color:var(--border-subtle)] text-slate-500 hover:text-red-400 transition-colors duration-200">Clear</button>}
              </div>
              <div className="flex gap-2 mt-3">
                <input data-testid="note-input" value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()} placeholder="Add a note…" className="flex-1 bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-3 py-2 text-sm text-white focus:border-gold focus:outline-none" />
                <button data-testid="add-note" onClick={addNote} className="px-3 py-2 text-xs flex items-center gap-1 border border-gold text-gold hover:bg-[rgba(212,175,55,0.1)] transition-colors duration-200"><StickyNote size={13} />Add</button>
              </div>
            </div>

            <div className="mt-5 border-t border-[color:var(--border-subtle)] pt-4">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Activity Timeline</div>
              <div className="space-y-2 max-h-56 overflow-y-auto" data-testid="activity-timeline">
                {(sel.activities || []).length === 0 && <div className="text-xs text-slate-500">No activity yet.</div>}
                {[...(sel.activities || [])].reverse().map((a, i) => (
                  <div key={i} className="flex gap-2 text-xs">
                    <span>{ACT_ICON[a.type] || "•"}</span>
                    <div className="flex-1">
                      <div className="text-slate-200">{a.text}</div>
                      <div className="text-[10px] text-slate-600">{relTime(a.at)}{a.by ? ` · ${a.by}` : ""}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-5 border-t border-[color:var(--border-subtle)] pt-4">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5"><Sparkles size={12} />AI Sales Agent</div>
              {sel.pending_followup?.draft && <div className="mb-2 text-[11px] text-gold flex items-center gap-1.5">✍️ AI auto-drafted a follow-up for this lead — review & send below.</div>}
              {botUser && <button data-testid="copy-connect-link" onClick={() => copyConnect(sel)} className="w-full mb-2 py-1.5 text-xs flex items-center justify-center gap-1.5 border border-[color:var(--border-subtle)] text-slate-300 hover:border-gold transition-colors duration-200"><Send size={12} />Copy Telegram connect link (auto-captures chat ID)</button>}
              <button data-testid="gen-followup-btn" onClick={genFollowup} className="w-full py-2 text-xs flex items-center justify-center gap-1.5 border border-gold text-gold hover:bg-[rgba(212,175,55,0.1)] transition-colors duration-200"><Sparkles size={13} />Generate follow-up for "{sel.status}"</button>
              {(fu.loading || fu.draft) && (
                <div className="mt-3 space-y-2">
                  {fu.loading ? <Spinner /> : (
                    <>
                      <textarea data-testid="followup-draft" value={fu.draft} onChange={(e) => setFu({ ...fu, draft: e.target.value })} rows={6} className="w-full bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-3 py-2 text-sm text-slate-200 focus:border-gold focus:outline-none" />
                      {fu.broker_link && <div className="text-[11px] text-slate-500">Broker link auto-inserted ({fu.broker}): <span className="text-gold break-all">{fu.broker_link}</span></div>}
                      <div>
                        <label className="text-[10px] uppercase tracking-wider text-slate-500">Lead Telegram chat ID {sel.telegram_chat_id ? "" : "(lead must have messaged the bot)"}</label>
                        <div className="flex gap-2 mt-1">
                          <input data-testid="lead-tg-chat" value={tgChat} onChange={(e) => setTgChat(e.target.value)} placeholder="e.g. 123456789" className="flex-1 bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-3 py-2 text-xs font-mono text-white focus:border-gold focus:outline-none" />
                          <button data-testid="save-tg-chat" onClick={saveChat} className="px-3 py-2 text-xs border border-[color:var(--border-subtle)] text-slate-300 hover:border-gold transition-colors duration-200">Save</button>
                        </div>
                      </div>
                      <button data-testid="send-tg-btn" onClick={sendTg} disabled={!fu.draft} className="btn-gold w-full py-2 text-sm flex items-center justify-center gap-1.5 disabled:opacity-50"><Send size={14} />Send to Telegram</button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add lead modal */}
      {adding && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setAdding(false)} />
          <form onSubmit={addLead} className="relative w-full max-w-sm surface p-5" data-testid="add-lead-form">
            <div className="flex items-center mb-4"><h3 className="font-head text-lg text-white">Add Lead</h3><button type="button" onClick={() => setAdding(false)} data-testid="add-lead-close" className="ml-auto p-1 text-slate-400 hover:text-white"><X size={18} /></button></div>
            {["name", "email", "phone", "country"].map((f) => (
              <input key={f} required={f === "name"} placeholder={f[0].toUpperCase() + f.slice(1)} value={form[f]} onChange={(e) => setForm({ ...form, [f]: e.target.value })}
                data-testid={`lead-input-${f}`} className="w-full mb-3 bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-3 py-2 text-sm text-white focus:border-gold focus:outline-none" />
            ))}
            <div className="flex gap-2 mb-4">
              <select value={form.interest} onChange={(e) => setForm({ ...form, interest: e.target.value })} className="flex-1 bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-3 py-2 text-sm text-white">
                {["Gold", "Bitcoin", "Both"].map((o) => <option key={o}>{o}</option>)}
              </select>
              <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="flex-1 bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-3 py-2 text-sm text-white">
                {["Facebook", "Instagram", "Website", "Telegram", "Manual", "Referral"].map((o) => <option key={o}>{o}</option>)}
              </select>
            </div>
            <button data-testid="submit-lead" className="btn-gold w-full py-2 text-sm">Add Lead</button>
          </form>
        </div>
      )}
      {showDigest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowDigest(false)} />
          <div className="relative w-full max-w-sm surface p-5" data-testid="digest-modal">
            <h3 className="font-head text-lg text-white mb-1">Morning Briefing DM</h3>
            <p className="text-xs text-slate-400 mb-4">Har subah 8 AM (Dubai) aapke Telegram pe ek briefing aayegi — kal ka performance, hot leads, aur due + overdue follow-ups, sab ek message me. Apna Telegram chat ID daalo (bot ko ek baar message karke @userinfobot se le sakte ho).</p>
            <input data-testid="digest-chat-input" value={adminChat} onChange={(e) => setAdminChat(e.target.value)} placeholder="e.g. 123456789" className="w-full mb-4 bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-3 py-2 text-sm font-mono text-white focus:border-gold focus:outline-none" />
            <div className="flex gap-2">
              <button data-testid="digest-save" onClick={saveDigest} className="btn-gold flex-1 py-2 text-sm">Save</button>
              <button data-testid="digest-send-now" onClick={sendDigestNow} className="flex-1 py-2 text-sm border border-[color:var(--border-subtle)] text-slate-300 hover:border-gold transition-colors duration-200">Send now</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
const Info = ({ k, v, icon }) => (
  <div className="flex items-start justify-between gap-3"><span className="text-slate-500 flex items-center gap-1.5">{icon}{k}</span><span className="text-slate-200 text-right break-all">{v}</span></div>
);
